
import React from 'react'
import { CheckCircle, Play, Award } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useContentProgress } from '@/hooks/useContentProgress'

interface LearnContent {
  id: string
  title: string
  duration: string
  completed: boolean
  content_type: 'video' | 'reading' | 'quiz' | 'infographic'
  video_url?: string
  infographic_url?: string
  content_text?: string
  order_index: number
}

interface LessonListProps {
  lessons: LearnContent[]
  currentLessonId: string
  onLessonSelect: (lessonId: string) => void
  moduleId?: string
  isContentCompleted: (contentId: string) => boolean
}

const LessonList = ({ lessons, currentLessonId, onLessonSelect, moduleId, isContentCompleted }: LessonListProps) => {

  const getLessonIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Play className="w-4 h-4" />
      case 'quiz':
        return <Award className="w-4 h-4" />
      case 'infographic':
        return <CheckCircle className="w-4 h-4" />
      default:
        return <CheckCircle className="w-4 h-4" />
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'video':
        return 'bg-blue-100 text-blue-800'
      case 'quiz':
        return 'bg-purple-100 text-purple-800'
      case 'infographic':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-green-100 text-green-800'
    }
  }

  return (
    <Card className="bg-white rounded-xl shadow-sm border border-gray-100">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold text-gray-900">Course Content</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
          {lessons.map((lesson, index) => {
            const isCompleted = isContentCompleted(lesson.id)
            console.log('LessonList - lesson:', lesson.id, 'isCompleted:', isCompleted)
            return (
              <div 
                key={lesson.id} 
                className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                  currentLessonId === lesson.id ? 'bg-purple-50 border-l-4 border-purple-600' : ''
                }`}
                onClick={() => onLessonSelect(lesson.id)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                    isCompleted 
                      ? 'bg-green-100 text-green-600' 
                      : 'bg-gray-100 text-gray-400'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="w-3 h-3" />
                    ) : (
                      <span className="text-xs font-medium">{index + 1}</span>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className={`font-medium text-sm truncate ${
                        isCompleted ? 'text-gray-900' : 'text-gray-700'
                      }`}>
                        {lesson.title}
                      </h3>
                      <Badge variant="outline" className={`${getTypeColor(lesson.content_type)} text-xs px-1 py-0`}>
                        {lesson.content_type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {getLessonIcon(lesson.content_type)}
                      <span>{lesson.duration}</span>
                      {isCompleted && (
                        <span className="text-green-600 font-medium">• Completed</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

export default LessonList
